import { message } from "antd";
import { useRef, useState } from "react";
import * as xlsx from "xlsx";
import {
  SPREADSHEET_ACCEPT,
  findSpreadsheetFileError,
  findSpreadsheetSizeError,
} from "../../../utils/excelImport";
import { uploadCustomerPrice } from "../clientService";

/** Excel-ийн эхний sheet-ээс productSku-тай мөрүүдийг уншина. */
const parsePriceFile = (arrayBuffer) => {
  const workbook = xlsx.read(arrayBuffer, { type: "array" });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];

  return xlsx.utils
    .sheet_to_json(worksheet)
    .filter((item) => item.productSku)
    .map((item) => ({ ...item, productSku: String(item.productSku).trim() }));
};

/**
 * B2B харилцагчийн үнийн Excel файлыг сонгож хуулах логик.
 *
 * Файл сонгох `<input>` нь хүснэгтээс гадна нэг л удаа render хийгддэг
 * (мөр бүрт биш) тул аль харилцагчийн файл болохыг ref-ээр дамжуулна.
 *
 *   const { openFileDialog, fileInput, uploadingId } = useCustomerPriceUpload({ onUploaded });
 *   // openFileDialog(record) — мөрийн товчинд, fileInput — render дотор
 *   // uploadingId — хуулж буй харилцагчийн `_id` (товч дээр loading харуулна)
 */
const useCustomerPriceUpload = ({ onUploaded } = {}) => {
  const inputRef = useRef(null);
  const recordRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);

  const openFileDialog = (record) => {
    recordRef.current = record;
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    const record = recordRef.current;
    // Ижил файлыг дахин сонгоход onChange ажиллахын тулд input-г цэвэрлэнэ
    e.target.value = "";
    if (!file || !record) return;

    // Сервер рүү илгээхээс ӨМНӨ шалгана — эс тэгвээс хэт том файл ч илгээгдэж,
    // буцаж ирсэн ерөнхий хариунаас хэрэглэгч юуг засахаа мэдэхгүй үлдэнэ
    const fileError = findSpreadsheetFileError(file) ?? findSpreadsheetSizeError(file);
    if (fileError) {
      message.error(fileError);
      return;
    }

    const reader = new FileReader();
    reader.onload = async ({ target }) => {
      // Файл уншихад гарсан алдааг interceptor мэдэхгүй тул энд мэдэгдэнэ
      let products;
      try {
        products = parsePriceFile(target.result);
      } catch (error) {
        console.error("Customer price file parse error: ", error);
        message.error("Үнийн файлыг уншиж чадсангүй.");
        return;
      }

      // Сервер ийм үед «Excel олдсонгүй» гэж л хариулдаг тул хэрэглэгч юуг
      // засахаа мэдэхгүй үлддэг — илгээхээс өмнө тодорхой хэлнэ
      if (!products.length) {
        message.error(
          "Файлаас бараа олдсонгүй. «productSku» баганад барааны SKU кодыг бөглөнө үү."
        );
        return;
      }

      setUploadingId(record._id);
      try {
        const data = await uploadCustomerPrice({
          customerId: record._id,
          products,
        });

        if (data?.statusCode === 200) {
          message.success(data?.result?.message || "Үнэ амжилттай шинэчлэгдлээ", 5);
        }
        onUploaded?.();
      } catch (error) {
        // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
        console.error("Customer price upload error: ", error);
      } finally {
        setUploadingId(null);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const fileInput = (
    <input
      style={{ display: "none" }}
      type="file"
      accept={SPREADSHEET_ACCEPT}
      ref={inputRef}
      onChange={handleChange}
    />
  );

  return { openFileDialog, fileInput, uploadingId };
};

export default useCustomerPriceUpload;
